import React, {useCallback, useContext} from 'react';

import {NumberInput} from 'src/components/input';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';

import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {FormSection} from '@pb/kit-component-form-section';
import {Integer} from '@pb/kit-component-formatters';
import Label from '@pb/kit-component-label';
import {LocaleContext} from '@pb/kit-context-locale';


import {AccountLimitsSectionSkeleton} from './account-limits-section-skeleton';
import {ALERTS_PER_EMAIL, SEARCH_RESULTS_PER_PAGE} from './constants';

import type {AccountLimits} from 'src/openapi/admin/models/AccountLimits';
import type {Error} from 'src/openapi/admin/models/Error';

type Props = {
    loading: boolean,
    loadingError: Error | null,
    disabled?: boolean,
    accountLimits: AccountLimits,
    onChange: React.Dispatch<React.SetStateAction<AccountLimits>>,
    onFieldChange: (parameterName: string) => void,
};

export const AccountLimitsSection: React.FC<Props> = props => {
    const {loading, disabled, accountLimits, onChange, onFieldChange, loadingError} = props;
    const {locale} = useContext(LocaleContext);

    const onMaxSearchesPerResultsChange = useCallback((maxResultPerPage: number) => {
        onFieldChange('Max search results per page');
        onChange(limits => ({...limits, maxResultPerPage: +maxResultPerPage}));
    }, [onFieldChange, onChange]);

    const onMaxSearchesPerDayBlur = useCallback((maxPagePerDay?: number) => {
        onFieldChange('Max search pages per day');
        onChange(limits => ({...limits, maxPagePerDay: maxPagePerDay || 0}));
    }, [onFieldChange, onChange]);

    const onMaxAlertsPerEmailChange = useCallback((maxAlertPerEmail: number) => {
        onFieldChange('Max alerts per email');
        onChange(limits => ({...limits, maxAlertPerEmail: +maxAlertPerEmail}));
    }, [onFieldChange, onChange]);

    return (
        <Skeleton view={<AccountLimitsSectionSkeleton/>} shown={loading}>
            <ServerResponseHandler
                serverError={loadingError}
                smallView={true}
                sectionName="Account Limits"
                showDefaultError={false}>
                <FormSection headline="Results Limits">
                    <ResponsiveElementGroup>
                        <WidthItem width={300}>
                            <Label id="max-result-per-page-label" htmlFor="max-result-per-page-combobox">
                                Max search results per page
                            </Label>
                            <ComboBox
                                id="max-result-per-page-combobox"
                                aria-labelledby="max-result-per-page-label"
                                disabled={disabled}
                                value={accountLimits.maxResultPerPage}
                                onChange={onMaxSearchesPerResultsChange}>
                                {SEARCH_RESULTS_PER_PAGE.map((count: number) => (
                                    <ComboboxItem key={count} value={count} caption={count.toString()}/>
                                ))}
                            </ComboBox>
                        </WidthItem>
                        <NumberInput
                            width={300}
                            label={<span>Max search pages per day</span>}
                            onBlur={onMaxSearchesPerDayBlur}
                            readOnly={disabled}
                            placeholder="Max search pages per day"
                            value={accountLimits.maxPagePerDay || 0}
                            maxValue={9999}
                            displayedValue={Integer.format(accountLimits.maxPagePerDay || 0, locale)}
                        />
                        <WidthItem width={300}>
                            <Label id="max-alerts-per-email-label" htmlFor="max-alerts-per-email-combobox">
                                Max alerts per email
                            </Label>
                            <ComboBox
                                id="max-alerts-per-email-combobox"
                                aria-labelledby="max-alerts-per-email-label"

                                value={accountLimits.maxAlertPerEmail}
                                disabled={disabled}
                                onChange={onMaxAlertsPerEmailChange}>
                                {ALERTS_PER_EMAIL.map(({value, caption}) => (
                                    <ComboboxItem key={value} value={value} caption={caption}/>
                                ))}
                            </ComboBox>
                        </WidthItem>
                    </ResponsiveElementGroup>
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
